import { inputLabel, inputText } from '../../fonts';
import { colors } from '../../colors';
import get from 'lodash.get';
const inputColors = {
  blue: {
    200: '#D4D6DA',
    400: '#A5ACB5',
    500: '#223044',
    600: '#79838F',
    800: '#4D596A'
  },
  gold: {
    200: '#ECE7D6',
    400: '#D9CFAD',
    500: '#A18732',
    600: '#C7B784',
    800: '#B49F5B'
  },
  primary: {
    200: '#CAD9F2',
    400: '#98B5E1',
    500: '#0A4A8D',
    600: '#6591CA',
    800: '#336EAE'
  },
  secondary: {
    200: '#B3E2FB',
    400: '#93D2F4',
    500: '#499CC9',
    600: '#77C1E9',
    800: '#5EAFDB'
  },
  tertiary: {
    200: '#BDF9FF',
    400: '#A4F4FF',
    500: '#6ED8FF',
    600: '#8EECFF',
    800: '#7CE3FF'
  },
  lightGray: {
    200: '#FAFAFA',
    400: '#F5F5F5',
    500: '#BDBDBD',
    600: '#EEEEEE',
    800: '#E0E0E0'
  },
  darkGray: {
    200: '#9E9E9E',
    400: '#757575',
    500: '#212121',
    600: '#616161',
    800: '#424242'
  },
  success: {
    200: '#D9EFE9',
    400: '#03906A',
    500: '#03906A',
    600: '#005F44'
  },
  error: {
    200: '#FBE1E1',
    400: '#CC3736',
    500: '#CC3736',
    600: '#841E1D'
  },
  warning: {
    200: '#FFE9CA',
    400: '#DE9E41',
    500: '#8C5505',
    600: '#D68612'
  },
  white: {
    500: '#FFFFFF'
  }
};
const FormControl = {
  baseStyle: () => {
    return {
      flexBasis: {
        sm: '100%',
        md: '306',
        lg: '306'
      },
      mb: '4'
    };
  }
};
const FormControlLabel = {
  baseStyle: () => {
    return {
      _text: {
        fontSize: inputLabel.fontSize.base,
        fontWeight: 500,
        color: 'darkGray.500',
        textTransform: 'uppercase'
      },
      mb: '1',
      ml: '4',
      _astrick: {
        color: 'darkGray.500',
        ml: 1,
        fontSize: inputLabel.fontSize.base
      },
      _disabled: {
        _text: {
          color: 'darkGray.600'
        }
      }
    };
  }
};
const FormControlErrorMessage = {
  baseStyle: () => {
    return {
      mt: '1',
      ml: '4',
      _text: {
        fontSize: 'sm',
        color: 'error.500'
      },
      _stack: {
        space: 1,
        alignItems: 'center'
      },
      _dark: {
        _text: {
          color: 'error.500'
        }
      }
    };
  }
};
const FormControlHelperText = {
  baseStyle: _ref => {
    let {
      colorScheme
    } = _ref;
    const color = get(inputColors, [colorScheme, '500']);
    return {
      mt: '1',
      ml: '4',
      _text: {
        fontSize: 'sm',
        color
      },
      _dark: {
        _text: {
          color
        }
      }
    };
  }
};
const Input = {
  baseStyle: _ref2 => {
    let {
      colorScheme
    } = _ref2;
    const borderColor = get(inputColors, [colorScheme, '600']);
    return {
      fontFamily: 'body',
      fontWeight: inputText.fontWeight.base,
      borderRadius: 2,
      overflow: 'hidden',
      _disabled: {
        borderColor: 'darkGray.400',
        bg: 'transparent',
        _web: {
          disabled: true,
          cursor: 'not-allowed'
        }
      },
      _web: {
        outlineWidth: '0',
        overflow: 'auto',
        style: {
          outline: 'none'
        },
        cursor: 'auto'
      },
      _input: {
        bg: 'white',
        flex: 1,
        w: '100%',
        h: '100%',
        pl: '4',
        py: '4'
      },
      borderColor,
      bg: 'white.500',
      placeholderTextColor: 'darkGray.400',
      color: 'darkGray.500',
      _stack: {
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'hidden'
      },
      _hover: {
        borderColor
      },
      _invalid: {
        borderColor: 'error.500',
        _hover: {
          borderColor: 'error.500'
        },
        _stack: {
          style: {
            outlineWidth: '0',
            boxShadow: `0 0 0 0px ${colors.error['500']}`
          }
        }
      },
      _focus: {
        borderColor,
        _hover: {
          borderColor
        },
        _invalid: {
          borderColor: 'error.500',
          _hover: {
            borderColor: 'error.500'
          },
          _stack: {
            style: {
              outlineWidth: '0',
              boxShadow: `0 0 0 0px ${colors.error['500']}`
            }
          }
        },
        _ios: {
          selectionColor: 'darkGray.500'
        },
        _android: {
          selectionColor: 'darkGray.500'
        },
        _disabled: {
          placeholderTextColor: 'darkGray.400',
          _hover: {
            borderColor: 'darkGray.400'
          }
        },
        _stack: {
          style: {
            outlineWidth: '0',
            boxShadow: `0 0 0 0px ${inputColors[colorScheme]}.600`
          }
        }
      }
    };
  },
  variants: {
    outline: {
      borderWidth: '1',
      _focus: {
        bg: 'white'
      }
    }
  },
  sizes: {
    sm: {
      fontSize: inputText.fontSize
    }
  }
};
const Select = {
  baseStyle: () => {
    return {
      selection: {
        start: 0
      },
      _customDropdownIconProps: {
        color: 'primary.500',
        size: '20',
        px: '4',
        mr: 0
      },
      _hover: {
        borderColor: '#6591CA'
      },
      _focus: {
        borderColor: '#6591CA'
      },
      _disabled: {
        bg: 'muted.100',
        placeholderTextColor: 'darkGray.400'
      },
      _invalid: {
        borderColor: 'error.500'
      },
      _webSelect: {
        style: {
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: 0,
          zIndex: 1
        }
      },
      _web: {
        pointerEvents: 'none'
      },
      _actionSheetBody: {
        w: '100%'
      },
      _actionSheetContent: {}
    };
  },
  defaultProps: {
    optimized: true
  }
};
export { FormControl, FormControlLabel, FormControlErrorMessage, FormControlHelperText, Input, Select };
//# sourceMappingURL=index.js.map